'use client';

import React, { useMemo } from 'react';
import { Card } from 'src/components/PulseUI';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useGetProviderVolumeQuery } from 'src/api/redux';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { claimsOrEncounters } from 'src/components/Profiles/shared/widgets/utils';
import { getBenchmarkState, HERO_COLOR } from 'src/components/Profiles/shared/widgets/shared/benchmark';
import TargetBadge from 'src/components/Profiles/shared/widgets/shared/TargetBadge';
import DataSummary from 'src/components/Profiles/shared/widgets/shared/DataSummary';
import WidgetHeader from 'src/components/Profiles/shared/widgets/WidgetHeader';

const CompactRatio: React.FC = () => {
  const widgetConfig = useWidgetConfig();
  const widget = widgetConfig?.widget ?? null;
  const dateRangeLabel = widgetConfig?.dateRangeLabel ?? '';
  const { entityDetails } = useProfile();
  const entityId = entityDetails?.id;
  const { showEncountersTotals } = useFlags();

  const numeratorCodeSetId = widget?.numerator_code_set?.id;
  const numeratorEncounterId = widget?.numerator_encounter?.id;
  const denominatorCodeSetId = widget?.denominator_code_set?.id;
  const denominatorEncounterId = widget?.denominator_encounter?.id;

  const hasNumeratorId = !!(numeratorCodeSetId || numeratorEncounterId);
  const hasDenominatorId = !!(denominatorCodeSetId || denominatorEncounterId);

  const minYear = widgetConfig?.minYear;
  const maxYear = widgetConfig?.maxYear;
  const dateRange =
    widget?.min_year != null || widget?.max_year != null
      ? { gte: String(minYear), lte: String(maxYear) }
      : undefined;

  const { data: numeratorData, isLoading: numeratorLoading } =
    useGetProviderVolumeQuery(
      {
        providerId: entityId || '',
        filter_set: numeratorCodeSetId ?? undefined,
        cohort_id: numeratorEncounterId ?? undefined,
        dateRange,
      },
      { skip: !entityId || !hasNumeratorId }
    );

  const { data: denominatorData, isLoading: denominatorLoading } =
    useGetProviderVolumeQuery(
      {
        providerId: entityId || '',
        filter_set: denominatorCodeSetId ?? undefined,
        cohort_id: denominatorEncounterId ?? undefined,
        dateRange,
      },
      { skip: !entityId || !hasDenominatorId }
    );

  const isLoading = numeratorLoading || denominatorLoading;

  const numeratorLabel =
    widget?.numerator_code_set?.label ??
    widget?.numerator_encounter?.label ??
    'Numerator';
  const denominatorLabel =
    widget?.denominator_code_set?.label ??
    widget?.denominator_encounter?.label ??
    'Denominator';

  const benchmark = widget?.benchmark ?? null;
  const benchmarkOperator = widget?.benchmark_operator ?? 'greater_than';

  const numeratorVolumeType =
    widget?.numerator_volume_type ?? widget?.volume_type ?? 'patients';
  const denominatorVolumeType =
    widget?.denominator_volume_type ?? widget?.volume_type ?? 'patients';

  const numeratorVolumeLabel =
    numeratorVolumeType === 'claims' ? 'Claims' : 'Patients';
  const denominatorVolumeLabel =
    denominatorVolumeType === 'claims' ? 'Claims' : 'Patients';

  const ratioData = useMemo(() => {
    if (!numeratorData || !denominatorData) return null;

    // For a cohort (encounter) slot, the claims role reads encounters when flagged.
    const numeratorVolume =
      numeratorVolumeType === 'claims'
        ? Number(
            claimsOrEncounters(
              numeratorData,
              !!numeratorEncounterId,
              showEncountersTotals
            ) ?? 0
          )
        : Number(numeratorData[numeratorVolumeType] ?? 0);
    const denominatorVolume =
      denominatorVolumeType === 'claims'
        ? Number(
            claimsOrEncounters(
              denominatorData,
              !!denominatorEncounterId,
              showEncountersTotals
            ) ?? 0
          )
        : Number(denominatorData[denominatorVolumeType] ?? 0);

    const ratio =
      denominatorVolume > 0
        ? Math.round((numeratorVolume / denominatorVolume) * 10) / 10
        : 0;

    return { ratio, numeratorVolume, denominatorVolume };
  }, [
    numeratorData,
    denominatorData,
    numeratorVolumeType,
    denominatorVolumeType,
    numeratorEncounterId,
    denominatorEncounterId,
    showEncountersTotals,
  ]);

  const hasData = ratioData?.ratio !== null && ratioData?.ratio !== undefined;

  const benchmarkState =
    hasData && benchmark !== null
      ? getBenchmarkState(ratioData!.ratio!, benchmark, benchmarkOperator)
      : null;

  const heroColor = benchmarkState
    ? HERO_COLOR[benchmarkState]
    : 'text-sky-600';

  return (
    <Card
      className="p-5 flex flex-col gap-3 bg-white h-full"
      data-testid="compact-ratio"
      role="region"
      aria-label={widget?.label ?? 'Treating Volume Ratio Analysis'}
    >
      <WidgetHeader
        fallbackTitle="Ratio Analysis"
        showDateRange={false}
        trailing={
          benchmark !== null ? (
            <TargetBadge target={benchmark} testId="compact-ratio-target-badge" />
          ) : undefined
        }
      />
      <div className="flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <WidgetLoading data-testid="compact-ratio-loading" className="flex items-center justify-center py-8" />
        ) : !hasData ? (
          <WidgetEmpty data-testid="compact-ratio-empty" variant="full" />
        ) : (
          <div
            className="flex flex-col gap-3"
            data-testid="compact-ratio-content"
          >
            {/* Hero ratio */}
            <div
              className="flex items-center gap-3"
              data-testid="compact-ratio-hero-section"
            >
              <span
                className={`text-5xl font-bold tabular-nums ${heroColor}`}
                data-testid="compact-ratio-hero"
                data-benchmark-state={benchmarkState ?? 'none'}
                aria-label={`Ratio: ${ratioData!.ratio!.toFixed(1)}${
                  benchmarkState ? `, ${benchmarkState} benchmark` : ''
                }`}
              >
                {ratioData!.ratio!.toFixed(1)}
              </span>
              <div className="flex flex-col">
                <span
                  className="text-sm text-neutral-900"
                  data-testid="compact-ratio-label"
                >
                  <span className="font-bold">{numeratorLabel}</span>
                  {' '}
                  <span className="text-xs font-semibold text-neutral-400">{numeratorVolumeLabel}</span>
                  {' per '}
                  <span className="font-bold">{denominatorLabel}</span>
                  {' '}
                  <span className="text-xs font-semibold text-neutral-400">{denominatorVolumeLabel}</span>
                </span>
                <span
                  className="text-xs text-neutral-400"
                  data-testid="compact-ratio-date-range"
                >
                  per year from {dateRangeLabel}
                </span>
              </div>
            </div>

            {/* Data Summary */}
            <DataSummary
              rows={[
                {
                  label: (
                    <div className="flex flex-col">
                      <span>{numeratorLabel}</span>
                      <span className="text-xs font-semibold text-neutral-400">{numeratorVolumeLabel}</span>
                    </div>
                  ),
                  value: ratioData!.numeratorVolume,
                },
                {
                  label: (
                    <div className="flex flex-col">
                      <span>{denominatorLabel}</span>
                      <span className="text-xs font-semibold text-neutral-400">{denominatorVolumeLabel}</span>
                    </div>
                  ),
                  value: ratioData!.denominatorVolume,
                },
              ]}
              testIdPrefix="compact-ratio-data-summary"
            />
          </div>
        )}
      </div>
    </Card>
  );
};

export default CompactRatio;
